import { AxiosResponse } from 'axios';
import { KeyWithAllowedValueType } from 'src/dto/Types';
import apiProvider from './provider';
import { getAuthenticationHeaders, getErrorCode, getErrorMessage, getErrorMessageForBlob } from './utils';

type ApiResponse = {
  isSuccess: boolean;
  errorMessage?: string;
  errorCode?: string;
  data?: KeyWithAllowedValueType;
};


function isAbortError(err: any): boolean {
  return err && (err.name === 'AbortError' || err.code === 'ERR_CANCELED' || err.message === 'canceled');
}

// Generic response wrapper - matches typical API structure
export interface ApiResponseWrapper<T = Record<string, unknown>> {
  data: T;
}

export const GET = async (
  url: string,
  params?: any,
  responseType?: 'blob' | undefined,
  headers: any = getAuthenticationHeaders(),
  signal?: AbortSignal
): Promise<any> => {
  const isBlob = responseType === 'blob';
  try {
    const response: AxiosResponse<ApiResponseWrapper | Blob> = await apiProvider.get(url, {
      params,
      headers,
      responseType,
      ...(signal ? { signal } : {}),
    });
    if (!isBlob) {
      const responseData = response.data as any;
      if (responseData && typeof responseData.statusCode === 'number' && responseData.statusCode >= 400) {
        const msg = Array.isArray(responseData.message)
          ? responseData.message.join(', ')
          : responseData.message;
        return {
          isSuccess: false,
          errorMessage: msg || responseData.error || 'Something went wrong',
          status: response.status,
        };
      }
    }
    const dataToReturn: {
      isSuccess: true;
      data: any;
      status: number;
      filename?: string;
    } = {
      isSuccess: true,
      data: isBlob ? response.data : (response.data as ApiResponseWrapper)?.data,
      status: response.status,
    };
    if (isBlob) {
      const disposition = response.headers['content-disposition'];
      let filename = '';
      if (disposition) {
        const matches = disposition.match(/filename="(.*)"/);
        filename = matches && matches.length && matches.length > 0 ? decodeURI(matches[1]) : '';
      }
      dataToReturn.filename = filename;
    }
    return dataToReturn;
  } catch (err) {
    // Ignore abort errors
    if (isAbortError(err)) {
      return {
        errorMessage: 'Request canceled',
        isSuccess: false,
        status: undefined,
      };
    }
    const errorMessage = isBlob ? await getErrorMessageForBlob(err) : getErrorMessage(err);
    return {
      errorMessage,
      errorCode: getErrorCode(err),
      isSuccess: false,
      status: err && err.response && err.response.status,
    };
  }
};

export const POST = async (
  url: string,
  body: any,
  headers = getAuthenticationHeaders(),
  isBlob = false,
  signal?: AbortSignal
): Promise<any> => {
  try {
    const meta: any = {
      headers,
      ...(signal ? { signal } : {}),
    };
    if (isBlob) {
      meta.responseType = 'blob';
    }
    const response: AxiosResponse<ApiResponseWrapper | Blob> = await apiProvider.post(url, body, meta);
    if (!isBlob) {
      const responseData = response.data as any;
      if (responseData && typeof responseData.statusCode === 'number' && responseData.statusCode >= 400) {
        const msg = Array.isArray(responseData.message)
          ? responseData.message.join(', ')
          : responseData.message;
        return {
          isSuccess: false,
          errorMessage: msg || responseData.error || 'Something went wrong',
        };
      }
    }
    const dataToReturn: {
      isSuccess: true;
      data: any;
      filename?: string;
    } = {
      isSuccess: true,
      data: isBlob ? response.data : (response.data as ApiResponseWrapper)?.data,
    };
    if (isBlob) {
      const disposition = response.headers['content-disposition'];
      let filename = '';
      if (disposition) {
        const matches = disposition.match(/filename="(.*)"/);
        filename = matches && matches.length && matches.length > 0 ? decodeURI(matches[1]) : '';
      }
      dataToReturn.filename = filename;
    }
    return dataToReturn;
  } catch (err) {

    if (isAbortError(err)) {
      return {
        errorMessage: 'Request canceled',
        isSuccess: false,
      };
    }
    const errorMessage = isBlob ? await getErrorMessageForBlob(err) : getErrorMessage(err);
    return {
      errorMessage,
      errorCode: getErrorCode(err),
      isSuccess: false,
    };
  }
};

export const PATCH = async (
  url: string,
  body,
  headers = getAuthenticationHeaders()
): Promise<any> => {
  try {
    const response = await apiProvider.patch(url, body, {
      headers,
    });
    const responseData = response?.data as any;
    if (responseData && typeof responseData.statusCode === 'number' && responseData.statusCode >= 400) {
      const msg = Array.isArray(responseData.message)
        ? responseData.message.join(', ')
        : responseData.message;
      return {
        isSuccess: false,
        errorMessage: msg || responseData.error || 'Something went wrong',
      };
    }
    return {
      isSuccess: true,
      data: responseData?.data,
    };
  } catch (err) {
    const errorMessage = getErrorMessage(err);
    return {
      errorMessage,
      isSuccess: false,
    };
  }
};

export const DELETE = async (
  url: string,
  data?: any,
  headers = getAuthenticationHeaders()
): Promise<any> => {
  try {
    const response = await apiProvider.delete(url, {
      headers,
      data,
    });
    return {
      isSuccess: true,
      data: response?.data?.data,
    };
  } catch (err) {
    const errorMessage = getErrorMessage(err);
    return {
      errorMessage,
      isSuccess: false,
    };
  }
};

export const DELETE_QUERIED = async (
  url: string,
  params?: any,
  headers = getAuthenticationHeaders()
): Promise<any> => {
  try {
    const response: AxiosResponse<ApiResponseWrapper> = await apiProvider.delete(url, {
      params,
      headers,
    });
    return {
      isSuccess: true,
      data: response.data.data,
    };
  } catch (err) {
    const errorMessage = getErrorMessage(err);
    return {
      errorMessage,
      isSuccess: false,
    };
  }
};

const dataHeaders = {
  JPG: 'image/*',
  JPEG: 'image/*',
  PNG: 'image/*',
  PDF: 'application/*',
  DOCX: 'application/*',
  XLSX: 'application/*',
  CSV: 'text/csv',
  XLS: 'application/*',
  DOC: 'application/*',
  EML: 'message/rfc822',
  MSG: 'application/vnd.ms-outlook',
};

export const PUT = async (url: string, extension: string, file: any): Promise<any> => {
  try {
    const response = await apiProvider.put(url, file, {
      headers: {
        'Content-Type': dataHeaders[extension.toUpperCase()],
      },
    });
    return {
      isSuccess: true,
    };
  } catch (err) {
    const errorMessage = getErrorMessage(err);
    return {
      errorMessage,
      isSuccess: false,
    };
  }
};

export const UPDATE = async (
  url: string,
  body: any,
  headers = getAuthenticationHeaders()
): Promise<ApiResponse> => {
  try {
    const response = await apiProvider.put(url, body, {
      headers,
    });
    return {
      isSuccess: true,
      data: response?.data?.data,
    };
  } catch (err) {
    const errorMessage = getErrorMessage(err);
    return {
      errorMessage,
      isSuccess: false,
    };
  }
};
